import React from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import CurrencyFormatter from '../../../utils/text/CurrencyFormatter';
import Typography from '../../typography/Typography';
import { chartColors } from '../hardware/AssetAgeChart';

interface DataPoint {
  month: string;
  amount: number;
  price: number;
}

const TechnicalLicenseChart = ({
  data,
  activeKey,
}: {
  data: DataPoint[];
  activeKey: string;
}) => {
  const activeIndex = data.findIndex((item) => item.month === activeKey);

  const formatXAxis = (str: string) => {
    const month = parseInt(str.split('#')[1]);
    return new Date(2026, month - 1).toLocaleString('default', {
      month: 'short',
    });
  };

  return (
    <div className="w-full h-[240px] p-4 rounded-xl border border-slate-200 dark:border-slate-800">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
          activeIndex={activeIndex}
        >
          <CartesianGrid
            strokeDasharray="8 8"
            vertical={false}
            className="stroke-slate-100 dark:stroke-slate-900"
          />

          <XAxis
            dataKey="month"
            axisLine={false}
            tickLine={false}
            tickFormatter={formatXAxis}
            tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 600 }}
            dy={10}
          />

          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            dx={-5}
          />

          <Tooltip
            cursor={{ fill: 'rgba(99, 102, 241, 0.04)' }}
            content={({ active, payload }) =>
              active && payload && payload.length ? (
                <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border border-slate-200 dark:border-slate-800 p-3 rounded-lg shadow-xl ring-1 ring-black/5">
                  <Typography tag="p" type="small">
                    {payload[0].payload.month.split('#')[1] +
                      '/' +
                      payload[0].payload.month.split('#')[0]}
                  </Typography>
                  <div className="space-y-1 mt-2">
                    <div className="flex justify-between items-center gap-6">
                      <Typography tag="span" type="small">
                        Aantal
                      </Typography>
                      <Typography tag="span" type="small" color="blue">
                        {payload[0].value}
                      </Typography>
                    </div>
                    <div className="flex justify-between items-center gap-6">
                      <Typography tag="span" type="small">
                        Prijs
                      </Typography>
                      <Typography tag="span" type="small" color="blue">
                        <CurrencyFormatter price={payload[1]?.value} />
                      </Typography>
                    </div>
                  </div>
                </div>
              ) : null
            }
          />

          <Bar dataKey="amount" barSize={24} radius={[4, 4, 0, 0]}>
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  index === activeIndex
                    ? '#6366f1'
                    : chartColors[index % chartColors.length]
                }
                fillOpacity={
                  activeIndex === -1 || index === activeIndex ? 1 : 0.4
                }
              />
            ))}
          </Bar>

          <Line
            type="monotone"
            dataKey="price"
            stroke={'#fc8a8f'}
            strokeWidth={2}
            dot={{
              r: 4,
              fill: '#fc8a8f',
              strokeWidth: 1,
              stroke: '#fff',
            }}
            activeDot={{ r: 6, strokeWidth: 0 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TechnicalLicenseChart;
